import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Sparkles, Share2, ArrowRight } from 'lucide-react';
import { FaTwitter, FaLinkedin, FaEnvelope } from 'react-icons/fa';
import { BLOG_POSTS, getRelatedPosts } from '../data/blogData';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const BlogDetails = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <section className="min-h-screen py-24 bg-black pt-32 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-4xl font-serif text-white mb-4">Post Not Found</h2>
          <p className="text-gray-400 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium hover:shadow-lg hover:shadow-yellow-500/30 transition">
            <ArrowLeft size={18} /> Back to Blog
          </Link>
        </div>
      </section>
    );
  }

  const relatedPosts = getRelatedPosts(post.id, post.category, 3);
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      className="min-h-screen py-24 bg-gradient-to-b from-black via-zinc-900 to-black pt-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-600/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse delay-1000" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div variants={fadeUp} className="mb-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors group">
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </Link>
        </motion.div>

        <motion.div variants={fadeUp} className="mb-10">
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mb-4">
            <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/20 text-xs">
              {post.category}
            </span>
            <span className="flex items-center gap-1"><Calendar size={14} /> {formatDate(post.date)}</span>
            <span className="flex items-center gap-1"><Clock size={14} /> {post.readTime}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4">{post.title}</h1>
          <div className="flex items-center gap-4">
            <img src={post.author.avatar} alt={post.author.name} className="w-12 h-12 rounded-full object-cover border-2 border-yellow-500/30" />
            <div>
              <div className="text-white font-medium">{post.author.name}</div>
              <div className="text-sm text-gray-400">{post.author.role}</div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-yellow-500/10 mb-12">
          <img src={post.image} alt={post.title} className="w-full h-auto max-h-[500px] object-cover" />
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="prose prose-invert prose-yellow max-w-none prose-lg prose-headings:text-white prose-p:text-gray-300 prose-a:text-yellow-400 prose-strong:text-white prose-li:text-gray-300"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <motion.div variants={fadeUp} className="mt-12 pt-8 border-t border-white/10">
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm font-medium flex items-center gap-2"><Share2 size={16} /> Share this article</span>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-yellow-500/20 transition">
              <FaTwitter size={18} />
            </a>
            <a href={`https://linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-yellow-500/20 transition">
              <FaLinkedin size={18} />
            </a>
            <a href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(window.location.href)}`} className="p-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-yellow-500/20 transition">
              <FaEnvelope size={18} />
            </a>
          </div>
        </motion.div>

        {relatedPosts.length > 0 && (
          <motion.div variants={fadeUp} className="mt-16">
            <h3 className="text-2xl font-serif font-bold text-white mb-8">You Might Also Like</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((related) => (
                <Link key={related.id} to={`/blog/${related.slug}`} className="group bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-yellow-500/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-yellow-500/10">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={related.image} alt={related.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-white group-hover:text-yellow-400 transition-colors line-clamp-2">{related.title}</h4>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(related.date)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </motion.section>
  );
};

export default BlogDetails;